import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-lg' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img 
                src="https://static.readdy.ai/image/56592420ad0c806e82c83516867f5679/d1b89c1c9827b7fc761aa972a1497809.png" 
                alt="Smart Site Solutions" 
                className="h-10 w-auto"
              />
              <span className={`text-xl font-bold transition-colors ${
                isScrolled ? 'text-[#1A2332]' : 'text-[#1A2332]'
              }`}>Smart Site Solutions</span>
            </div>
            
            <div className="hidden md:flex items-center space-x-8">
              <button 
                onClick={() => scrollToSection('services')}
                className={`text-base font-medium transition-colors hover:text-[#4A9FF5] ${
                  isScrolled ? 'text-gray-700' : 'text-gray-700'
                }`}
              >
                Services
              </button>
              <button 
                onClick={() => scrollToSection('process')}
                className={`text-base font-medium transition-colors hover:text-[#4A9FF5] ${
                  isScrolled ? 'text-gray-700' : 'text-gray-700'
                }`}
              >
                Process
              </button>
              <button 
                onClick={() => scrollToSection('benefits')}
                className={`text-base font-medium transition-colors hover:text-[#4A9FF5] ${
                  isScrolled ? 'text-gray-700' : 'text-gray-700'
                }`}
              >
                Benefits
              </button>
              <button 
                onClick={() => scrollToSection('contact')}
                className="bg-[#4A9FF5] text-white px-6 py-2.5 rounded-full font-medium hover:bg-[#2B7FE8] transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                Get Started
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-white via-[#F0F8FF] to-white pt-20">
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 right-10 w-96 h-96 bg-[#4A9FF5] rounded-full opacity-10 blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 left-10 w-80 h-80 bg-[#3DD5D5] rounded-full opacity-10 blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full h-full">
            <div className="absolute inset-0 bg-[linear-gradient(rgba(74,159,245,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(74,159,245,0.03)_1px,transparent_1px)] bg-[size:50px_50px]"></div>
          </div>
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
          <h1 className="text-6xl md:text-7xl font-bold text-[#1A2332] leading-tight mb-6 animate-fade-in-up">
            Websites & Automation Systems<br />
            <span className="bg-gradient-to-r from-[#4A9FF5] to-[#3DD5D5] bg-clip-text text-transparent">
              Built for Service-Based Businesses
            </span>
          </h1>

          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-10 leading-relaxed animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            Capture every lead, answer every call, and run your business 24/7—without adding staff or working more hours.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-5 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
            <button 
              onClick={() => scrollToSection('contact')}
              className="bg-[#4A9FF5] text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-[#2B7FE8] transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              See How It Works for You
            </button>
            <button 
              onClick={() => scrollToSection('process')}
              className="bg-white text-[#4A9FF5] px-8 py-4 rounded-full text-lg font-semibold border-2 border-[#4A9FF5] hover:bg-[#4A9FF5] hover:text-white transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              View the Process
            </button>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-line text-3xl text-[#4A9FF5]"></i>
        </div>
      </section>

      {/* Pain Points Section */}
      <section className="py-24 bg-[#F7F9FC]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-12">
            <div className="inline-block px-4 py-2 rounded-lg bg-[#4A9FF5] text-white font-medium mb-4">
              The Challenge
            </div>
            <h2 className="text-5xl font-bold text-[#1A2332] mb-4">
              How Much Revenue Are You Losing Each Month?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-3xl p-10 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#4A9FF5]/20 to-[#3DD5D5]/20 flex items-center justify-center mb-6">
                <i className="ri-phone-line text-3xl text-[#4A9FF5]"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1A2332] mb-4">Missed Calls = Lost Revenue</h3>
              <p className="text-gray-600 leading-relaxed">
                After-hours calls, busy lines, and voicemail tags mean potential customers are calling your competitors instead. Every missed call costs you money.
              </p>
            </div>

            <div className="bg-white rounded-3xl p-10 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#4A9FF5]/20 to-[#3DD5D5]/20 flex items-center justify-center mb-6">
                <i className="ri-time-line text-3xl text-[#4A9FF5]"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1A2332] mb-4">Hours Lost to Admin Work</h3>
              <p className="text-gray-600 leading-relaxed">
                Scheduling appointments, answering the same questions, and chasing leads eats up time you should be spending on billable work or growing your business.
              </p>
            </div>

            <div className="bg-white rounded-3xl p-10 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#4A9FF5]/20 to-[#3DD5D5]/20 flex items-center justify-center mb-6">
                <i className="ri-computer-line text-3xl text-[#4A9FF5]"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1A2332] mb-4">Your Website Isn't Converting</h3>
              <p className="text-gray-600 leading-relaxed">
                An outdated or confusing website makes visitors leave without contacting you. If your site doesn't build trust instantly, you're losing business to competitors with better online presence.
              </p>
            </div>

            <div className="bg-white rounded-3xl p-10 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#4A9FF5]/20 to-[#3DD5D5]/20 flex items-center justify-center mb-6">
                <i className="ri-alert-line text-3xl text-[#4A9FF5]"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1A2332] mb-4">Leads Falling Through the Cracks</h3>
              <p className="text-gray-600 leading-relaxed">
                Without organized systems, follow-ups get forgotten, appointments are missed, and qualified leads disappear. Disorganization directly impacts your bottom line.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 rounded-lg bg-[#4A9FF5]/10 text-[#4A9FF5] font-medium mb-4">
              Our Services
            </div>
            <h2 className="text-5xl font-bold text-[#1A2332] mb-4">
              Two Systems That Work Together
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A professional website that converts visitors into leads, plus automation that captures and manages those leads 24/7.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Website Design Service */}
            <div className="group relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#4A9FF5] to-[#3DD5D5] p-12 shadow-xl hover:shadow-2xl transition-all duration-300">
              <div className="relative z-10">
                <div className="text-white/80 text-sm font-medium mb-3 tracking-wider">SERVICE 01</div>
                <h3 className="text-4xl font-bold text-white mb-6">
                  Conversion-Focused<br />Website Design
                </h3>
                <div className="space-y-4 mb-8">
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <p className="text-white/90 text-lg">Built to turn visitors into paying customers</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <p className="text-white/90 text-lg">Mobile-optimized so you never lose a lead</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <p className="text-white/90 text-lg">Clear messaging that builds trust immediately</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <p className="text-white/90 text-lg">Fast, professional, and easy to update</p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
                    <i className="ri-layout-line text-3xl text-white"></i>
                  </div>
                  <Link 
                    to="/website-design"
                    onClick={() => window.scrollTo(0, 0)}
                    className="group/btn inline-flex items-center space-x-2 bg-white text-[#4A9FF5] px-6 py-3 rounded-full font-semibold hover:bg-gray-50 transition-all duration-300 hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer"
                    title="Discover how a website built for your business can capture leads, save time, and grow revenue without extra effort."
                  >
                    <span>See How Websites Work for You</span>
                    <i className="ri-arrow-right-line text-lg group-hover/btn:translate-x-1 transition-transform"></i>
                  </Link>
                </div>
              </div>
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
            </div>

            {/* AI Automation Service */}
            <div className="group relative overflow-hidden rounded-3xl bg-white border-2 border-gray-200 p-12 shadow-xl hover:shadow-2xl transition-all duration-300 hover:border-[#4A9FF5]">
              <div className="relative z-10">
                <div className="text-[#4A9FF5] text-sm font-medium mb-3 tracking-wider">SERVICE 02</div>
                <h3 className="text-4xl font-bold text-[#1A2332] mb-6">
                  24/7 Lead Capture<br />&amp; Automation
                </h3>
                <div className="space-y-4 mb-8">
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 rounded-full bg-[#4A9FF5]/20 flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-[#4A9FF5] text-sm"></i>
                    </div>
                    <p className="text-gray-700 text-lg">Answer calls and book appointments automatically</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 rounded-full bg-[#4A9FF5]/20 flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-[#4A9FF5] text-sm"></i>
                    </div>
                    <p className="text-gray-700 text-lg">Capture leads even when you're busy or closed</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 rounded-full bg-[#4A9FF5]/20 flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-[#4A9FF5] text-sm"></i>
                    </div>
                    <p className="text-gray-700 text-lg">Eliminate repetitive admin tasks that waste time</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 rounded-full bg-[#4A9FF5]/20 flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-[#4A9FF5] text-sm"></i>
                    </div>
                    <p className="text-gray-700 text-lg">Operate around the clock without hiring staff</p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#4A9FF5]/20 to-[#3DD5D5]/20 flex items-center justify-center">
                    <i className="ri-robot-line text-3xl text-[#4A9FF5]"></i>
                  </div>
                  <Link 
                    to="/ai-automation"
                    onClick={() => window.scrollTo(0, 0)}
                    className="group/btn inline-flex items-center space-x-2 bg-[#4A9FF5] text-white px-6 py-3 rounded-full font-semibold hover:bg-[#2B7FE8] transition-all duration-300 hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer"
                    title="Discover how AI can manage leads, appointments, and follow-ups—saving time and boosting revenue without extra staff."
                  >
                    <span>See AI Automation in Action</span>
                    <i className="ri-arrow-right-line text-lg group-hover/btn:translate-x-1 transition-transform"></i>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="process" className="py-24 bg-[#F7F9FC]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-[#1A2332] mb-4">Simple Process, Fast Results</h2>
            <p className="text-xl text-gray-600">Most clients are live within 2-4 weeks</p>
          </div>

          <div className="relative">
            {/* Connection Line */}
            <div className="hidden md:block absolute top-24 left-0 right-0 h-1 bg-gradient-to-r from-[#4A9FF5] via-[#3DD5D5] to-[#4A9FF5] opacity-30"></div>

            <div className="grid md:grid-cols-4 gap-8 relative">
              {/* Step 1 */}
              <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 text-center">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#4A9FF5] to-[#3DD5D5] flex items-center justify-center text-white text-2xl font-bold mx-auto mb-6">
                  1
                </div>
                <div className="w-12 h-12 rounded-full bg-[#4A9FF5]/10 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-search-line text-2xl text-[#4A9FF5]"></i>
                </div>
                <h3 className="text-xl font-bold text-[#1A2332] mb-3">Discovery Call</h3>
                <p className="text-gray-600 leading-relaxed">
                  We learn how your business operates and what's costing you time or revenue
                </p>
              </div>

              {/* Step 2 */}
              <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 text-center">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#4A9FF5] to-[#3DD5D5] flex items-center justify-center text-white text-2xl font-bold mx-auto mb-6">
                  2
                </div>
                <div className="w-12 h-12 rounded-full bg-[#4A9FF5]/10 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-pencil-line text-2xl text-[#4A9FF5]"></i>
                </div>
                <h3 className="text-xl font-bold text-[#1A2332] mb-3">Build Your System</h3>
                <p className="text-gray-600 leading-relaxed">
                  We design your website and automation to solve your specific challenges
                </p>
              </div>

              {/* Step 3 */}
              <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 text-center">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#4A9FF5] to-[#3DD5D5] flex items-center justify-center text-white text-2xl font-bold mx-auto mb-6">
                  3
                </div>
                <div className="w-12 h-12 rounded-full bg-[#4A9FF5]/10 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-settings-3-line text-2xl text-[#4A9FF5]"></i>
                </div>
                <h3 className="text-xl font-bold text-[#1A2332] mb-3">Test & Refine</h3>
                <p className="text-gray-600 leading-relaxed">
                  You review everything and we adjust until it's exactly right
                </p>
              </div>

              {/* Step 4 */}
              <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 text-center">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#4A9FF5] to-[#3DD5D5] flex items-center justify-center text-white text-2xl font-bold mx-auto mb-6">
                  4
                </div>
                <div className="w-12 h-12 rounded-full bg-[#4A9FF5]/10 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-rocket-line text-2xl text-[#4A9FF5]"></i>
                </div>
                <h3 className="text-xl font-bold text-[#1A2332] mb-3">Go Live</h3>
                <p className="text-gray-600 leading-relaxed">
                  Launch your system and start capturing leads immediately
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-start justify-between mb-12 gap-8">
            <h2 className="text-5xl font-bold text-[#1A2332] md:w-2/5">
              Why Smart Site Solutions?
            </h2>
            <p className="text-xl text-gray-600 md:w-1/2">
              We specialize in service-based businesses. Every system we build is designed to increase revenue and reduce the time you spend on admin work.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Large Feature Card */}
            <div className="md:col-span-2 bg-[#1A2332] rounded-3xl p-12 relative overflow-hidden group hover:scale-[1.02] transition-all duration-300">
              <div className="relative z-10">
                <h3 className="text-3xl font-bold text-white mb-4">Built for Service Businesses</h3>
                <p className="text-white/80 text-lg leading-relaxed mb-8">
                  We don't build generic websites. Every system is designed specifically for contractors, local services, and professionals who need to capture leads fast and operate efficiently.
                </p>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 rounded-full bg-[#4A9FF5] flex items-center justify-center">
                    <i className="ri-check-line text-2xl text-white"></i>
                  </div>
                  <span className="text-white font-medium">Proven Systems That Drive Results</span>
                </div>
              </div>
              <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#4A9FF5]/20 rounded-full blur-3xl"></div>
            </div>

            {/* Standard Feature Card */}
            <div className="bg-[#F7F9FC] rounded-3xl p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-[#4A9FF5] text-white text-sm font-medium mb-6">
                <i className="ri-star-fill mr-2"></i>
                Key Benefit
              </div>
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#4A9FF5]/20 to-[#3DD5D5]/20 flex items-center justify-center mx-auto mb-6">
                <i className="ri-focus-line text-3xl text-[#4A9FF5]"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1A2332] text-center mb-4">Revenue-Focused Design</h3>
              <p className="text-gray-600 text-center leading-relaxed">
                Every feature is built to capture more leads and save you time—not just look good
              </p>
            </div>

            {/* Standard Feature Card */}
            <div className="bg-[#F7F9FC] rounded-3xl p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-[#4A9FF5] text-white text-sm font-medium mb-6">
                <i className="ri-star-fill mr-2"></i>
                Key Benefit
              </div>
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#4A9FF5]/20 to-[#3DD5D5]/20 flex items-center justify-center mx-auto mb-6">
                <i className="ri-24-hours-line text-3xl text-[#4A9FF5]"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1A2332] text-center mb-4">Always On, Always Working</h3>
              <p className="text-gray-600 text-center leading-relaxed">
                Your systems capture leads and handle inquiries 24/7, even when you're off the clock
              </p>
            </div>

            {/* Standard Feature Card */}
            <div className="bg-[#F7F9FC] rounded-3xl p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-[#4A9FF5] text-white text-sm font-medium mb-6">
                <i className="ri-star-fill mr-2"></i>
                Key Benefit
              </div>
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#4A9FF5]/20 to-[#3DD5D5]/20 flex items-center justify-center mx-auto mb-6">
                <i className="ri-settings-4-line text-3xl text-[#4A9FF5]"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1A2332] text-center mb-4">Custom-Built for You</h3>
              <p className="text-gray-600 text-center leading-relaxed">
                No templates or cookie-cutter solutions—everything is tailored to your business
              </p>
            </div>

            {/* Standard Feature Card */}
            <div className="bg-[#F7F9FC] rounded-3xl p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-[#4A9FF5] text-white text-sm font-medium mb-6">
                <i className="ri-star-fill mr-2"></i>
                Key Benefit
              </div>
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#4A9FF5]/20 to-[#3DD5D5]/20 flex items-center justify-center mx-auto mb-6">
                <i className="ri-award-line text-3xl text-[#4A9FF5]"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1A2332] text-center mb-4">Reliable & Consistent</h3>
              <p className="text-gray-600 text-center leading-relaxed">
                Systems that work the same way every time, with no human error or missed steps
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-24 bg-[#F7F9FC]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-12">
            <div className="inline-block px-4 py-2 rounded-lg bg-[#4A9FF5] text-white font-medium mb-4">
              The Results
            </div>
            <h2 className="text-5xl font-bold text-[#1A2332] mb-4">
              What You Get When<br />You Work With Us
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#4A9FF5]/20 to-[#3DD5D5]/20 flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-line text-2xl text-[#4A9FF5]"></i>
                </div>
                <h3 className="text-xl font-bold text-[#1A2332]">Reclaim Hours Every Week</h3>
              </div>
              <p className="text-gray-600 leading-relaxed mb-4">
                Stop answering the same questions and scheduling appointments manually. Automation handles it so you can focus on revenue-generating work.
              </p>
              <div className="w-10 h-1 bg-[#4A9FF5] rounded-full"></div>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#4A9FF5]/20 to-[#3DD5D5]/20 flex items-center justify-center flex-shrink-0">
                  <i className="ri-user-add-line text-2xl text-[#4A9FF5]"></i>
                </div>
                <h3 className="text-xl font-bold text-[#1A2332]">Capture Every Lead</h3>
              </div>
              <p className="text-gray-600 leading-relaxed mb-4">
                No more missed calls or lost opportunities. Your system works 24/7 to capture leads even when you're busy, closed, or off the clock.
              </p>
              <div className="w-10 h-1 bg-[#4A9FF5] rounded-full"></div>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#4A9FF5]/20 to-[#3DD5D5]/20 flex items-center justify-center flex-shrink-0">
                  <i className="ri-trophy-line text-2xl text-[#4A9FF5]"></i>
                </div>
                <h3 className="text-xl font-bold text-[#1A2332]">Stand Out From Competitors</h3>
              </div>
              <p className="text-gray-600 leading-relaxed mb-4">
                A professional, modern website builds instant credibility and trust. Customers choose businesses that look established and reliable.
              </p>
              <div className="w-10 h-1 bg-[#4A9FF5] rounded-full"></div>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#4A9FF5]/20 to-[#3DD5D5]/20 flex items-center justify-center flex-shrink-0">
                  <i className="ri-refresh-line text-2xl text-[#4A9FF5]"></i>
                </div>
                <h3 className="text-xl font-bold text-[#1A2332]">Operate 24/7 Without Staff</h3>
              </div>
              <p className="text-gray-600 leading-relaxed mb-4">
                Your business runs smoothly around the clock without hiring additional staff or working longer hours yourself.
              </p>
              <div className="w-10 h-1 bg-[#4A9FF5] rounded-full"></div>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#4A9FF5]/20 to-[#3DD5D5]/20 flex items-center justify-center flex-shrink-0">
                  <i className="ri-line-chart-line text-2xl text-[#4A9FF5]"></i>
                </div>
                <h3 className="text-xl font-bold text-[#1A2332]">Increase Monthly Revenue</h3>
              </div>
              <p className="text-gray-600 leading-relaxed mb-4">
                Convert more website visitors into paying customers with systems designed to capture, qualify, and nurture every lead automatically.
              </p>
              <div className="w-10 h-1 bg-[#4A9FF5] rounded-full"></div>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#4A9FF5]/20 to-[#3DD5D5]/20 flex items-center justify-center flex-shrink-0">
                  <i className="ri-shield-check-line text-2xl text-[#4A9FF5]"></i>
                </div>
                <h3 className="text-xl font-bold text-[#1A2332]">Eliminate Human Error</h3>
              </div>
              <p className="text-gray-600 leading-relaxed mb-4">
                Automated systems handle every customer interaction consistently and professionally—no forgotten follow-ups or missed details.
              </p>
              <div className="w-10 h-1 bg-[#4A9FF5] rounded-full"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Who It's For Section */}
      <section className="py-24 bg-gradient-to-br from-[#4A9FF5]/10 via-white to-[#3DD5D5]/10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-[#1A2332] mb-4">Built for Service-Based Businesses</h2>
            <p className="text-xl text-gray-600">We specialize in businesses that need to capture leads fast and operate efficiently</p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 text-center group">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#4A9FF5] to-[#3DD5D5] flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-briefcase-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-[#1A2332] mb-3">Local Services</h3>
              <p className="text-gray-600 text-sm">Plumbers, electricians, cleaners, landscapers</p>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 text-center group">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#4A9FF5] to-[#3DD5D5] flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-hammer-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-[#1A2332] mb-3">Contractors & Trades</h3>
              <p className="text-gray-600 text-sm">HVAC, construction, roofing, remodeling</p>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 text-center group">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#4A9FF5] to-[#3DD5D5] flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-shopping-bag-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-[#1A2332] mb-3">Retail & E-commerce</h3>
              <p className="text-gray-600 text-sm">Online stores, boutiques, specialty shops</p>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 text-center group">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#4A9FF5] to-[#3DD5D5] flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-star-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-[#1A2332] mb-3">Professionals</h3>
              <p className="text-gray-600 text-sm">Consultants, coaches, agencies, advisors</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="py-24 bg-gradient-to-br from-[#1A2332] via-[#2B3F5C] to-[#1A2332] relative overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-[#4A9FF5]/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#3DD5D5]/20 rounded-full blur-3xl"></div>
        
        <div className="max-w-3xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Ready to Stop Losing Leads?
          </h2>
          <p className="text-xl text-gray-300 mb-12">
            See exactly how our systems will work for your business—free consultation, no pressure
          </p>

          <div className="bg-white rounded-3xl p-10 shadow-2xl">
            <form 
              id="contact_form"
              data-readdy-form
              action="https://readdy.ai/api/form/d58skd93kamldd15634g"
              method="POST"
              onSubmit={(e) => {
                e.preventDefault();
                const form = e.currentTarget;
                const formData = new FormData(form);
                
                fetch('https://readdy.ai/api/form/d58skd93kamldd15634g', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                  },
                  body: new URLSearchParams(formData as any).toString(),
                })
                .then(response => {
                  if (response.ok) {
                    alert('Thank you! We\'ll be in touch within 24 hours.');
                    form.reset();
                  } else {
                    alert('Something went wrong. Please try again.');
                  }
                })
                .catch(() => {
                  alert('Something went wrong. Please try again.');
                });
              }}
              className="space-y-5"
            >
              <input
                type="text"
                name="name"
                placeholder="Your Name"
                required
                className="w-full px-6 py-4 rounded-xl border-2 border-gray-200 focus:border-[#4A9FF5] focus:outline-none text-base transition-colors"
              />
              <input
                type="email"
                name="email"
                placeholder="Your Email"
                required
                className="w-full px-6 py-4 rounded-xl border-2 border-gray-200 focus:border-[#4A9FF5] focus:outline-none text-base transition-colors"
              />
              <input
                type="tel"
                name="phone"
                placeholder="Your Phone Number"
                required
                className="w-full px-6 py-4 rounded-xl border-2 border-gray-200 focus:border-[#4A9FF5] focus:outline-none text-base transition-colors"
              />
              <input
                type="text"
                name="business_name"
                placeholder="Business Name"
                required
                className="w-full px-6 py-4 rounded-xl border-2 border-gray-200 focus:border-[#4A9FF5] focus:outline-none text-base transition-colors"
              />
              <button
                type="submit"
                className="w-full bg-[#4A9FF5] text-white py-4 rounded-xl text-lg font-semibold hover:bg-[#2B7FE8] transition-all duration-300 hover:scale-[1.02] shadow-lg whitespace-nowrap cursor-pointer"
              >
                Get Your Free Consultation
              </button>
            </form>

            <div className="flex items-center justify-center space-x-6 mt-8 text-sm text-gray-600">
              <div className="flex items-center space-x-2">
                <i className="ri-check-line text-[#4A9FF5]"></i>
                <span>Response within 24 hours</span>
              </div>
              <div className="flex items-center space-x-2">
                <i className="ri-check-line text-[#4A9FF5]"></i>
                <span>No obligation</span>
              </div>
              <div className="flex items-center space-x-2">
                <i className="ri-check-line text-[#4A9FF5]"></i>
                <span>Free consultation</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1A2332] py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <img 
                  src="https://static.readdy.ai/image/56592420ad0c806e82c83516867f5679/d1b89c1c9827b7fc761aa972a1497809.png" 
                  alt="Smart Site Solutions" 
                  className="h-8 w-auto"
                />
                <span className="text-xl font-bold text-white">Smart Site Solutions</span>
              </div>
              <p className="text-gray-400">Websites and automation built for service businesses</p>
            </div>

            <div>
              <h4 className="text-base font-semibold text-white mb-4">Contact Us</h4>
              <div className="space-y-3">
                <div className="flex items-center space-x-2 text-gray-400">
                  <i className="ri-phone-line"></i>
                  <span>613-899-4468</span>
                </div>
                <div className="flex items-center space-x-2 text-gray-400">
                  <i className="ri-phone-line"></i>
                  <span>613-621-2422</span>
                </div>
                <div className="flex items-center space-x-2 text-gray-400">
                  <i className="ri-phone-line"></i>
                  <span>613-203-2762</span>
                </div>
                <div className="flex items-center space-x-2 text-gray-400">
                  <i className="ri-mail-line"></i>
                  <span>Smartsitesolutions1@gmail.com</span>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-base font-semibold text-white mb-4">Quick Links</h4>
              <div className="space-y-3">
                <button 
                  onClick={() => scrollToSection('services')}
                  className="block text-gray-400 hover:text-[#4A9FF5] transition-colors cursor-pointer"
                >
                  Services
                </button>
                <button 
                  onClick={() => scrollToSection('process')}
                  className="block text-gray-400 hover:text-[#4A9FF5] transition-colors cursor-pointer"
                >
                  Process
                </button>
                <button 
                  onClick={() => scrollToSection('contact')}
                  className="block text-gray-400 hover:text-[#4A9FF5] transition-colors cursor-pointer"
                >
                  Contact
                </button>
              </div>
            </div>

            <div>
              <h4 className="text-base font-semibold text-white mb-4">Get Started</h4>
              <button 
                onClick={() => scrollToSection('contact')}
                className="bg-[#4A9FF5] text-white px-6 py-3 rounded-full font-medium hover:bg-[#2B7FE8] transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Schedule Consultation
              </button>
            </div>
          </div>

          <div className="pt-8 border-t-2 border-[#4A9FF5]/20 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-400 text-sm">© 2025 Smart Site Solutions. All rights reserved.</p>
            <a 
              href="https://readdy.ai/?ref=logo" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-400 text-sm hover:text-[#4A9FF5] transition-colors cursor-pointer"
            >
              EST. 2024
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
